<template>
  <div class="flex flex-col w-full" style="min-height: 60vh">
    <div ref="body" class="flex-1 overflow-y-auto flex flex-col gap-2.5 pb-4 px-4">
      <div class="text-center text-[11px] text-gray-400 mb-0.5">
        ה-AI רואה את נתוני 30 הימים האחרונים · השיחה נמחקת בסגירה
      </div>

      <template v-for="(m, i) of messages" :key="i">
        <div
          v-if="m.role === 'user'"
          class="self-start max-w-[82%] bg-primary text-white px-3.5 py-2.5 text-[13px] leading-normal"
          style="border-radius: 15px 15px 15px 4px">
          {{ m.content }}
        </div>
        <div
          v-else
          class="self-end max-w-[92%] bg-white border border-gray-200 px-3.5 py-2.5 text-[13px] leading-relaxed text-gray-800 whitespace-pre-line"
          style="border-radius: 15px 15px 4px 15px">
          {{ m.content }}
        </div>
      </template>

      <!-- thinking indicator — sparkle orbit + shimmering gradient text -->
      <div
        v-if="thinking"
        class="self-end bg-white border border-gray-200 px-3.5 py-2.5"
        style="border-radius: 15px 15px 4px 15px">
        <div class="flex items-center gap-2.5 py-0.5">
          <span class="relative w-[22px] h-[22px] shrink-0 inline-flex items-center justify-center">
            <span class="thinking-orbit"/>
            <i class="pi pi-sparkles text-[10px] text-primary thinking-pulse"/>
          </span>
          <span class="thinking-text">קורא את הנתונים של העסק…</span>
        </div>
      </div>
    </div>

    <div class="shrink-0 pt-1.5 pb-3 px-4">
      <div v-if="chips.length && !thinking" class="flex gap-1.5 overflow-x-auto pb-2" style="scrollbar-width: none">
        <button
          v-for="q of chips"
          :key="q"
          class="shrink-0 text-[12px] font-semibold text-primary-700 bg-primary-50 border border-primary-200 rounded-full px-3.5 py-1.5"
          @click="emit('send', q)">
          {{ q }}
        </button>
      </div>
      <div class="flex items-center gap-2 border-[1.5px] border-gray-200 rounded-full py-1.5 ps-3.5 pe-2 bg-white">
        <i class="pi pi-sparkles text-primary-300 text-sm shrink-0"/>
        <input
          v-model="text"
          class="flex-1 min-w-0 border-none outline-none bg-transparent text-[12.5px] text-gray-900"
          placeholder="המשך שיחה…"
          :disabled="thinking"
          @keydown.enter="send"/>
        <button
          class="w-[30px] h-[30px] rounded-full shrink-0 flex items-center justify-center transition-colors duration-150"
          :style="text.trim() && !thinking
            ? {background: 'linear-gradient(135deg,#8864ee,#6831d0)'}
            : {background: '#f4f4f5'}"
          @click="send">
          <i class="pi pi-arrow-left text-xs" :class="text.trim() && !thinking ? 'text-white' : 'text-gray-400'"/>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {computed, nextTick, ref, watch} from 'vue';
import type {IAnalyticsAiChatMessage} from '@torim-app/types';

const props = defineProps<{
  messages: IAnalyticsAiChatMessage[];
  thinking: boolean;
  suggestedQuestions: string[];
}>();

const emit = defineEmits<{ send: [message: string] }>();

const body = ref<HTMLElement | null>(null);
const text = ref('');

const chips = computed(() => {
  const asked = new Set(props.messages.filter(m => m.role === 'user').map(m => m.content));
  return props.suggestedQuestions.filter(q => !asked.has(q)).slice(0, 2);
});

const send = () => {
  const q = text.value.trim();
  if (!q || props.thinking) return;
  text.value = '';
  emit('send', q);
};

watch([() => props.messages.length, () => props.thinking], () => {
  nextTick(() => {
    if (body.value) body.value.scrollTop = body.value.scrollHeight;
  });
});
</script>

<style scoped lang="scss">
.thinking-orbit {
  position: absolute;
  inset: 0;
  border-radius: 999px;
  border: 2px solid transparent;
  border-top-color: #8864ee;
  border-left-color: #e54ca1;
  animation: thinking-spin 1s linear infinite;
}

@keyframes thinking-spin {
  from { transform: rotate(0); }
  to { transform: rotate(360deg); }
}

.thinking-pulse {
  animation: thinking-pulse 1.2s ease-in-out infinite;
}

@keyframes thinking-pulse {
  0%, 100% { transform: scale(1); opacity: 1; }
  50% { transform: scale(0.75); opacity: 0.6; }
}

.thinking-text {
  font-size: 12px;
  font-weight: 600;
  background: linear-gradient(90deg, #6831d0, #e54ca1, #8864ee, #6831d0);
  background-size: 200% auto;
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  animation: thinking-shimmer 1.6s linear infinite;
}

@keyframes thinking-shimmer {
  from { background-position: 200% center; }
  to { background-position: -200% center; }
}
</style>
